import { useQuery } from "@tanstack/react-query";
import type { PlanSourceEntry, PlanView } from "@amber/profiles/contracts";
import { useCsrf } from "@amber/ui";
import { planPreviewQuery } from "../queries";

export function PlanEstimate({
  entries,
  backTo,
  planId,
}: {
  entries: readonly PlanSourceEntry[];
  backTo: string;
  planId: PlanView["id"] | null;
}) {
  const csrf = useCsrf();
  const query = useQuery({
    ...planPreviewQuery(
      {
        sources: entries.map((e) => ({ source_id: e.source_id, cadence_tier: e.cadence_tier })),
        back_to: backTo,
        owner_plan_id: planId,
      },
      csrf(),
    ),
    enabled: entries.length > 0,
  });
  const p = query.data;
  if (entries.length === 0) return <p className="m-0 text-md text-muted">Add a source to see the estimate.</p>;
  if (!p) return <p className="m-0 text-md text-muted">Estimating…</p>;
  return (
    <div className="text-md">
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted">New items</dt>
        <dd className="m-0 text-fg">about {p.items_per_month} per month</dd>
        <dt className="text-muted">Cost</dt>
        <dd className="m-0 text-fg">
          {p.cost_per_month.currency} {Number(p.cost_per_month.amount_decimal).toFixed(2)} per month
        </dd>
        <dt className="text-muted">History</dt>
        <dd className="m-0 text-fg">
          about {p.history_items} items, {p.history_cost.currency} {Number(p.history_cost.amount_decimal).toFixed(2)}{" "}
          once
        </dd>
        <dt className="text-muted">Scans</dt>
        <dd className="m-0 text-fg">
          {p.scans_per_month} per month
          {p.quota_units_per_month > 0 ? ` · about ${String(p.quota_units_per_month)} YouTube quota units` : ""}
        </dd>
      </dl>
      {p.unreachable.length > 0 ? (
        <ul className="m-0 mt-2 list-disc pl-4 text-muted">
          {p.unreachable.map((u) => (
            <li key={u}>{u}</li>
          ))}
        </ul>
      ) : null}
      <p className="m-0 mt-1 text-xs text-muted">
        Shared sources count with this plan&apos;s share of their cost. Estimates, not quotes.
      </p>
    </div>
  );
}
